import { useContext, useEffect, useState } from 'react';
import { Button, TableCell, TableRow, TableContainer, Table, TableHead, TableBody, Dialog, DialogActions, Checkbox, DialogContent, DialogTitle, FormControlLabel, FormGroup } from '@mui/material';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { StockCheckScheduleCountZoneType } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../../hooks/useLogError';
import { ScheduleCreateContext, ScheduleCreateValues } from '../../contexts/ScheduleCreateContext';
import { CloseDialogAction } from '../../../../components/shared/dialogs/CloseDialogAction';

const ScheduleCreateCountZoneTypesModal = () => {
  const { isCountZoneTypesModalOpen,
    handleClose,
    countZoneTypesData,
    handleCountZoneTypes,
    schedules,
    isCountZoneConfigEnabled } = useContext(ScheduleCreateContext) as ScheduleCreateValues;

  const { getCountZoneTypes } = useNswagClient();
  const [data, setData] = useState<StockCheckScheduleCountZoneType[] | undefined>([]);
  const [tempCountZoneTypesData, setTempCountZoneTypesData] = useState<StockCheckScheduleCountZoneType[] | undefined>([]);
  const [lastFetchedStore, setLastFetchedStore] = useState<string>('');
  const { t } = useTranslation('createStockCountSchedule');
  const { logError } = useLogError();

  const handleConfirm = () => {
    handleCountZoneTypes(tempCountZoneTypesData || []);
    setTempCountZoneTypesData([]);
  };

  const handleCountZoneTypeCheckboxChange = (countZoneTypeId: number, isChecked: boolean) => {
    if (isChecked) {
      const selected = data?.find(row => row.countZoneTypeId === countZoneTypeId);
      if (selected && !tempCountZoneTypesData?.some(z => z.countZoneTypeId === countZoneTypeId)) {
        setTempCountZoneTypesData(prev => [...(prev ?? []), selected]);
      }
    } else {
      setTempCountZoneTypesData(prev => (prev ?? []).filter(row => row.countZoneTypeId !== countZoneTypeId));
    }
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTempCountZoneTypesData(checked ? data ?? [] : []);
  };

  useEffect(() => {
    if (isCountZoneTypesModalOpen) {
      setTempCountZoneTypesData(countZoneTypesData ?? []);
    }
  }, [isCountZoneTypesModalOpen, countZoneTypesData]);

  useEffect(() => {
    if (!isCountZoneConfigEnabled) {
      return;
    }

    const allStoreNumbers = schedules.flatMap(s => s.storeNumbers || []);
    const uniqueStoreNumbers = Array.from(new Set(allStoreNumbers));
    const storeNumber = uniqueStoreNumbers[0] || '';
    
    if (!storeNumber) {
      setData([]);
      setLastFetchedStore('');
      return;
    }
    
    if (storeNumber !== lastFetchedStore) {
      setLastFetchedStore(storeNumber);
      
      getCountZoneTypes(storeNumber).then((result) => {
        if (result) {
          const countZoneTypes: StockCheckScheduleCountZoneType[] = result.map(r => ({
            countZoneTypeId: r.id,
            name: r.name,
            stockCheckScheduleDetailId: 0,
          }));
          countZoneTypes.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
          setData(countZoneTypes);
        } else {
          logError(result);
        }
      })
        .catch((error) => {
          logError(error);
        });
    }
  }, [schedules, lastFetchedStore, getCountZoneTypes, logError, isCountZoneConfigEnabled]);

  return (
    <Dialog open={isCountZoneTypesModalOpen}
      onClose={handleClose}
      maxWidth='md'
      fullWidth>
      <DialogTitle>
        {t('countZoneTypes.title')}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ overflowY: 'auto', border: '1px solid' }}
          style={{ maxHeight: '400px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell >{t('countZoneTypes.name')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((row) => (
                <TableRow key={row.countZoneTypeId}>
                  <TableCell style={{ display: 'flex', alignItems: 'center' }}  >
                    <Checkbox
                      color="primary"
                      inputProps={{
                        'aria-label': 'select all zones',
                      }}
                      checked={tempCountZoneTypesData?.some(z => z.countZoneTypeId === row.countZoneTypeId)}
                      onChange={(e) => handleCountZoneTypeCheckboxChange(row.countZoneTypeId ?? 0, e.target.checked)}
                    />
                    {row.name}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <FormGroup sx={{ pt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleSelectAllChange}
              />
            }
            label={t('countZoneTypes.selectAll')}
          />
        </FormGroup>
      </DialogContent>
      <DialogActions >
        <Button variant="secondary"
          size='lg'
          onClick={handleClose}> {t('cancel')}</Button>
        <Button variant="primary"
          size='lg'
          onClick={handleConfirm}>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleCreateCountZoneTypesModal;
